import React from 'react';
import { Badge, Divider, Typography, Box, Button, List, ListItem, ListItemButton, ListItemIcon, ListItemText, IconButton, Tooltip, Chip, Stack, Drawer, Tabs, Tab, Popper, Fade, Paper, Alert, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Link } from '@mui/material';
import { NotificationsNoneOutlined, Notifications, Info as InfoIcon, CheckCircle, WarningAmber, ShoppingCart, SupportAgent, LockReset, People, Close as CloseIcon, Refresh as RefreshIcon, Visibility as VisibilityIcon, MarkEmailRead as MarkEmailReadIcon, Sort as SortIcon, MarkEmailUnread as MarkEmailUnreadIcon, InfoOutlined, Clear as ClearIcon, AddShoppingCart } from '@mui/icons-material';
import SummarizeIcon from "@mui/icons-material/Summarize";
import { fetchCcrSubmission, fetchPurchaseRequestSubmission, markUnread } from '../../services/notifications';
import { formatDateTime } from "../../utilities/globalServiceUtils";
import { useNotifications  } from '../../context/NotificationContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getNotificationNavigationTarget } from '../../utilities/notificationNavigation';
import { useSite } from '../../context/siteContext';
import Swal from 'sweetalert2';
import ModalWrapper from '../ModalWrapper/ModalWrapper';
import { ROUTES } from '../../constants';
import { useCart } from '../../context/cartContext';
import { useAuth } from '../../context/authContext';
import { saveRequestListBulk } from '../../services/rsplService';
import { getProductsById } from '../../services/cartService';
import { NotificationEvents } from '../../constants/notificationEvents';
import formatCurrency from "../../utilities/currency";
import { ASSETS_BASE_URL } from "../../config";
import useBrandingAssets from "../../hooks/useBrandingAssets";

const buildPurchaseRequestCardSummary = (notification = {}) => {
	const data = notification?.data || {};
	let requestedBy = String(data?.requested_by_name || "").trim();
	if (!requestedBy) {
		const body = String(notification?.body || "");
		const match = body.match(/^(.+?)\s+requested help placing an order\./i);
		requestedBy = String(match?.[1] || "").trim();
	}
	const message = String(data?.message || "").trim();
	const items = Array.isArray(data?.items) ? data.items : [];
	const productCount = items.length;
	const totalFromData = String(data?.total_amount || "").trim();
	let total = totalFromData;
	if (!total) {
		const msg = message || String(notification?.body || "");
		const m = msg.match(/total\s*amount\s*:\s*([^\.\n]+)/i);
		if (m?.[1]) total = m[1].trim();
	}

	const parts = [];
	if (requestedBy) parts.push(`Requested by: ${requestedBy}`);
	if (message) parts.push(`Message: ${message}`);
	if (productCount > 0) parts.push(`Parts: ${productCount}`);
	if (total) parts.push(`Total: ${total}`);
	return parts.join(" • ");
};

const getPurchaseRequestSender = (notification = {}, submission = null) => {
	const bySubmission = String(submission?.requested_by?.user_name || "").trim();
	if (bySubmission) return bySubmission;
	const byData = String(notification?.data?.requested_by_name || "").trim();
	if (byData) return byData;
	const body = String(notification?.body || "");
	const match = body.match(/^(.+?)\s+requested help placing an order\./i);
	return String(match?.[1] || "").trim();
};

/**
 * Tell the notifications page to reload after the bell deletes something.
 *
 * The page already listens for this event. It is still needed now that clearing is
 * server-side, because clearing only removes *read* rows — the unread count does not move,
 * and the page's reload effect is keyed on `unread`, so nothing else would prompt it.
 *
 * @returns {void}
 */
const notifyClearedElsewhere = () => {
	window.dispatchEvent(new CustomEvent('notifications:cleared-updated'));
};

const NotificationBell = () => {
	const DRAWER_PAGE_SIZE = 50;
	const { items, unread, lastNewEventToken, markAllRead, markOneRead, markOneUnread, refreshNotifications, deleteRecipients, clearAllRead } = useNotifications();
	const { t } = useTranslation();
	const [open, setOpen] = React.useState(false);
	const [showNew, setShowNew] = React.useState(false);
	const buttonRef = React.useRef(null);
	const arrowRef = React.useRef(null);
	const [busy, setBusy] = React.useState(false);
	const [sortOrder, setSortOrder] = React.useState(t('GEOGRAPHIC.NEWEST_FIRST'));
	const [tab, setTab] = React.useState('unread');
	const [ccrModalOpen, setCcrModalOpen] = React.useState(false);
	const [ccrSubmission, setCcrSubmission] = React.useState(null);
	const [purchaseRequestModalOpen, setPurchaseRequestModalOpen] = React.useState(false);
	const [activePurchaseRequestNotification, setActivePurchaseRequestNotification] = React.useState(null);
	const [purchaseRequestSubmission, setPurchaseRequestSubmission] = React.useState(null);
	const [purchaseRequestLoading, setPurchaseRequestLoading] = React.useState(false);
	const [purchaseRequestProductMeta, setPurchaseRequestProductMeta] = React.useState({});
	const [addingToCart, setAddingToCart] = React.useState(false);
	const [addingToRequestList, setAddingToRequestList] = React.useState(false);
	const [visibleCount, setVisibleCount] = React.useState(DRAWER_PAGE_SIZE);
	const navigate = useNavigate();
	const { selectedSite, selectedSiteCurrency, siteData, updateSite } = useSite();
	const { stockImageUrl } = useBrandingAssets();
	const { addMultipleToCart } = useCart();
	const { user, hasPermission, canAddToCart, canAddToRequestList } = useAuth();
	const canShowAddToCart = canAddToCart();
	const canShowAddToRequestList = canAddToRequestList();
	const canAccessRequestsPage = hasPermission("requests_management.view_request_history");
	const newNotificationText = t('UI_TEXT.NEW_NOTIFICATION_POPUP', { defaultValue: 'You have a new notification.' });

	React.useEffect(() => {
		if (lastNewEventToken) {
			setShowNew(true);
			const t = setTimeout(() => setShowNew(false), 3000);
			return () => clearTimeout(t);
		}
	}, [lastNewEventToken]);

	// If the authoritative unread count settles back to 0 (e.g. the pushed
	// notification wasn't actually a new unread item for this user), dismiss
	// the popup immediately instead of leaving it visible for its full timeout.
	React.useEffect(() => {
		if (showNew && (!Number.isFinite(Number(unread)) || Number(unread) <= 0)) {
			setShowNew(false);
		}
	}, [unread, showNew]);

	const refresh = async () => {
		try {
			setBusy(true);
			await refreshNotifications(500, 0);
		} finally {
			setBusy(false);
		}
	};

	const toggle = async () => {
		const nxt = !open;
		setOpen(nxt);
		if (nxt) {
			await refresh();
		}
	};

	React.useEffect(() => {
		if (!open) return;
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tab, open]);

	React.useEffect(() => {
		setVisibleCount(DRAWER_PAGE_SIZE);
	}, [DRAWER_PAGE_SIZE, open, tab, sortOrder]);

	const base = items;
	const sortedNotifications = React.useMemo(() => {
		const arr = Array.isArray(base) ? [...base] : [];
		arr.sort((a, b) => {
			const da = new Date(a.created_at).getTime();
			const db = new Date(b.created_at).getTime();
			return sortOrder === t('GEOGRAPHIC.NEWEST_FIRST') ? (db - da) : (da - db);
		});
		return arr;
	}, [base, sortOrder, t]);

	const unreadTabCount = React.useMemo(
		() => sortedNotifications.filter((n) => !n.read_at).length,
		[sortedNotifications]
	);

	const readTabCount = React.useMemo(
		() => sortedNotifications.filter((n) => !!n.read_at).length,
		[sortedNotifications]
	);
	const visibleUnreadCount = Number.isFinite(Number(unread))
		? Number(unread)
		: unreadTabCount;
	const unreadTooltipLabel = React.useMemo(() => {
		if (showNew) return '';
		if (visibleUnreadCount <= 0) return t('UI_TEXT.NO_UNREAD_NOTIFICATIONS');
		const unreadNoun = visibleUnreadCount === 1
			? t('GENERAL.UNREAD_NOTIFICATION', { defaultValue: 'Unread Notification' })
			: t('UI_TEXT.UNREAD_NOTIFICATION', { defaultValue: 'Unread Notifications' });
		return `${t('UI_TEXT.YOU_HAVE')} ${visibleUnreadCount} ${unreadNoun}`;
	}, [showNew, t, visibleUnreadCount]);

	const filteredNotifications = React.useMemo(
		() => sortedNotifications.filter((n) => (tab === 'unread' ? !n.read_at : !!n.read_at)),
		[sortedNotifications, tab]
	);

	const dataToShow = React.useMemo(
		() => filteredNotifications.slice(0, visibleCount),
		[filteredNotifications, visibleCount]
	);

	const hasMoreNotifications = filteredNotifications.length > dataToShow.length;

	const latestNotification = React.useMemo(() => {
		const arr = Array.isArray(base) ? [...base] : [];
		arr.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
		return arr[0] || null;
	}, [base]);

	const getIconForType = (type) => {
		const map = {
			ADMIN_NEW_ORDER_PLACED: { icon: <ShoppingCart />, color: 'success' },
			USER_ORDER_SUCCESS: { icon: <ShoppingCart />, color: 'success' },
			ADMIN_NOTIFY_CUSTOMER_CARE: { icon: <SupportAgent />, color: 'secondary' },
			ADMIN_NEW_USER_REGISTERED: { icon: <People />, color: 'primary' },
			USER_FORGOT_PASSWORD: { icon: <WarningAmber />, color: 'warning' },
			USER_REQUESTLIST_PURCHASABLE: { icon: <InfoOutlined />, color: 'success' },
		};
		return map[type] || { icon: <InfoIcon color="action" />, color: 'default' };
	};

	const onMarkOne = async (recipient_id) => {
		if (!recipient_id) return;
		try { await markOneRead(recipient_id); } catch { }
	};

	const onMarkUnread = async (recipient_id) => {
		if (!recipient_id) return;
		try { await markOneUnread(recipient_id); } catch { }
	};

	// Debounce hook
	const useDebounce = (callback, delay) => {
		const timeoutRef = React.useRef(null);
		return React.useCallback((...args) => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			timeoutRef.current = setTimeout(() => {
				callback(...args);
			}, delay);
		}, [callback, delay]);
	};

	// Debounced handlers
	const onMarkOneDebounced = useDebounce(onMarkOne, 300);
	const onMarkUnreadDebounced = useDebounce(onMarkUnread, 300);

	/**
	 * Clear a single read notification.
	 *
	 * Deletes it server-side — the same call the notifications page's own row Delete button
	 * makes. This used to only add the id to a localStorage hide-list, which is why a cleared
	 * row stayed on the notifications page and came back after a refresh whenever the id it
	 * was filed under changed (socket-pushed rows arrive with `recipient_id: null`).
	 *
	 * @param {number} recipient_id - notification_recipients.id to delete.
	 * @returns {Promise<void>} Resolves once the row is gone and the page has been signalled.
	 */
	const onClear = async (recipient_id) => {
		if (!recipient_id) return;
		await deleteRecipients([recipient_id]);
		notifyClearedElsewhere();
	};

	const formatTime = (ts) => {
		try {
			const d = new Date(ts);
			const now = new Date();
			const diffMs = now - d;
			const sec = Math.floor(diffMs / 1000);
			if (sec < 60) return 'now';
			const min = Math.floor(sec / 60);
			if (min < 60) return `${min} mins ago`;
			const hr = Math.floor(min / 60);
			// yesterday check
			const y = new Date(now);
			y.setDate(now.getDate() - 1);
			const isYesterday = d.getDate() === y.getDate() && d.getMonth() === y.getMonth() && d.getFullYear() === y.getFullYear();
			if (isYesterday) return `yesterday at ${formatDateTime(d)}`;
			if (hr < 24) return `${hr} hours ago`;
			return formatDateTime(d);
		} catch {
			return String(ts);
		}
	};

	const resolveNotificationSiteNumber = React.useCallback((notification) => {
		const data = notification?.data || {};
		const siteNumber =
			data.site_number ||
			data.siteNumber ||
			data.site ||
			data.site_code ||
			data.siteCode ||
			null;
		if (siteNumber) return String(siteNumber);
		const siteId =
			data.site_id ||
			data.siteId ||
			data.site_id_fk ||
			null;
		if (!siteId || !Array.isArray(siteData)) return null;
		const match = siteData.find((s) => Number(s.site_id) === Number(siteId));
		return match?.customer_site_number ? String(match.customer_site_number) : null;
	}, [siteData]);

	const hasAccessToNotificationSite = React.useCallback((notification) => {
		const notifSite = resolveNotificationSiteNumber(notification);
		if (!notifSite) return true;
		if (!Array.isArray(siteData) || siteData.length === 0) return false;
		return siteData.some((s) => {
			const siteNum = s?.customer_site_number || s?.site_number || s?.site || s?.site_code;
			return siteNum && String(siteNum) === String(notifSite);
		});
	}, [resolveNotificationSiteNumber, siteData]);

	const isDifferentSite = React.useCallback((notification) => {
		const notifSite = resolveNotificationSiteNumber(notification);
		if (!notifSite || !selectedSite) return false;
		return String(notifSite) !== String(selectedSite);
	}, [resolveNotificationSiteNumber, selectedSite]);

	const isPurchaseRequestNotification = React.useCallback((notification) => {
		const eventKey = String(notification?.event_key || "").toUpperCase();
		return eventKey === NotificationEvents.USER_PURCHASE_REQUEST_SUBMITTED;
	}, []);

	const resolvePurchaseRequestSiteNumber = React.useCallback((notification, submission = null) => {
		const fromSubmission = submission?.site?.site_number;
		if (fromSubmission) return String(fromSubmission);
		return resolveNotificationSiteNumber(notification);
	}, [resolveNotificationSiteNumber]);

	const resolveProductImageSrc = React.useCallback((item = {}) => {
		const raw = item?.asset_data?.images?.[0];
		if (raw) {
			if (/^(https?:)?\/\//.test(raw) || raw.startsWith("data:")) return raw;
			const imgPath = raw.startsWith("/") ? raw : `/${raw}`;
			return `${ASSETS_BASE_URL}${imgPath}`;
		}
		return `${ASSETS_BASE_URL}/ProductImages/TMB/${item.product_number}.JPG`;
	}, []);

	const getPurchaseRequestItems = React.useCallback((notification, submission = null) => {
		const rawItems = submission?.products || notification?.data?.items;
		if (!Array.isArray(rawItems)) return [];
		return rawItems
			.map((item) => {
				const productId = Number(item?.product_id);
				const quantity = Number(item?.quantity || 1);
				const machineId = Number(item?.machine_id);
				const meta = purchaseRequestProductMeta?.[productId] || {};
				const rawPurchasable = meta?.purchasable ?? item?.purchasable;
				const purchasable =
					rawPurchasable === true ||
					rawPurchasable === 1 ||
					String(rawPurchasable).toLowerCase() === "1" ||
					String(rawPurchasable).toLowerCase() === "true";
				if (!Number.isFinite(productId) || productId <= 0) return null;
				if (!Number.isFinite(quantity) || quantity <= 0) return null;
				return {
					product_id: productId,
					machine_id: Number.isFinite(machineId) && machineId > 0 ? machineId : (Number(meta?.machine_id) || null),
					product_number: String(item?.product_number || meta?.product_number || "").trim(),
					product_name: String(item?.product_name || meta?.product_name || "").trim(),
					asset_data: meta?.asset_data || null,
					quantity,
					purchasable,
					unit_price: Number(meta?.[`price_${selectedSiteCurrency}`] ?? 0) || 0,
				};
			})
			.filter(Boolean);
	}, [purchaseRequestProductMeta, selectedSiteCurrency]);

	const handleOpenPurchaseRequestNotification = React.useCallback(async (notification) => {
		setActivePurchaseRequestNotification(notification || null);
		setPurchaseRequestSubmission(null);
		setPurchaseRequestProductMeta({});
		setPurchaseRequestModalOpen(true);

		const submissionId = notification?.data?.purchase_request_submission_id;
		setPurchaseRequestLoading(true);
		try {
			let submission = null;
			if (submissionId) {
				submission = await fetchPurchaseRequestSubmission(submissionId);
				if (submission) setPurchaseRequestSubmission(submission);
			}
			const siteNumber = resolvePurchaseRequestSiteNumber(notification, submission);
			const rows = submission?.products || notification?.data?.items || [];
			const productIds = Array.from(
				new Set(
					(Array.isArray(rows) ? rows : [])
						.map((item) => Number(item?.product_id))
						.filter((id) => Number.isFinite(id) && id > 0)
				)
			);
			if (siteNumber && productIds.length) {
				const productsResp = await getProductsById(productIds, siteNumber);
				const products = Array.isArray(productsResp?.data) ? productsResp.data : [];
				const metaMap = {};
				products.forEach((product) => {
					const id = Number(product?.product_id);
					if (Number.isFinite(id) && id > 0) metaMap[id] = product;
				});
				setPurchaseRequestProductMeta(metaMap);
			}
		} finally {
			setPurchaseRequestLoading(false);
		}
	}, [resolvePurchaseRequestSiteNumber]);

	const ensurePurchaseRequestSiteReady = React.useCallback(async (notification, submission = null) => {
		if (!hasAccessToNotificationSite(notification)) {
			const notifSite = resolvePurchaseRequestSiteNumber(notification, submission);
			await Swal.fire({
				icon: 'warning',
				title: 'Access removed',
				text: `You no longer have access to the ${notifSite || 'selected'} location. Please contact system administrator for further details.`,
				toast: true,
				position: 'top-end',
				timer: 4000,
				showConfirmButton: false,
			});
			return null;
		}
		const notifSite = resolvePurchaseRequestSiteNumber(notification, submission);
		if (!notifSite) return null;
		const shouldPrompt = notifSite && selectedSite && String(notifSite) !== String(selectedSite);
		if (shouldPrompt) {
			const result = await Swal.fire({
				icon: 'warning',
				title: 'Switch Customer Location?',
				text: 'This action will apply to the notification site.',
				showCancelButton: true,
				confirmButtonText: 'Yes, Switch Customer Location',
				cancelButtonText: t('BUTTON.CANCEL'),
			});
			if (!result.isConfirmed) return null;
			updateSite(String(notifSite), siteData || []);
		}
		return String(notifSite);
	}, [hasAccessToNotificationSite, resolvePurchaseRequestSiteNumber, selectedSite, siteData, t, updateSite]);

	const handleAddPurchaseRequestItemsToCart = React.useCallback(async (items) => {
		const notification = activePurchaseRequestNotification;
		if (!notification || !items.length) return;
		const siteNumber = await ensurePurchaseRequestSiteReady(notification, purchaseRequestSubmission);
		if (!siteNumber) return;

		const confirm = await Swal.fire({
			icon: 'question',
			title: 'Add to cart?',
			text: `Add ${items.length} item(s) to cart for site ${siteNumber}?`,
			showCancelButton: true,
			confirmButtonText: 'Yes, Add to Cart',
			cancelButtonText: t('BUTTON.CANCEL'),
		});
		if (!confirm.isConfirmed) return;

		setAddingToCart(true);
		try {
			const products = items.map((item) => ({
				ProductID: item.product_id,
				Quantity: Number(item.quantity) || 1,
				Site: siteNumber,
			}));
			await addMultipleToCart(products);
		} finally {
			setAddingToCart(false);
		}
	}, [activePurchaseRequestNotification, addMultipleToCart, ensurePurchaseRequestSiteReady, purchaseRequestSubmission, t]);

	const handleAddPurchaseRequestItemsToRequestList = React.useCallback(async (items) => {
		const notification = activePurchaseRequestNotification;
		if (!notification || !items.length) return;
		const missingMachine = items.filter((item) => !item.machine_id);
		if (missingMachine.length > 0) {
			await Swal.fire({
				icon: 'warning',
				title: 'Missing machine mapping',
				text: `${missingMachine.length} item(s) cannot be added to request list because machine_id is missing.`,
			});
			return;
		}
		const siteNumber = await ensurePurchaseRequestSiteReady(notification, purchaseRequestSubmission);
		if (!siteNumber) return;

		const confirm = await Swal.fire({
			icon: 'question',
			title: 'Add to request list?',
			text: `Add ${items.length} item(s) to request list for site ${siteNumber}?`,
			showCancelButton: true,
			confirmButtonText: 'Yes, Add to Request List',
			cancelButtonText: t('BUTTON.CANCEL'),
		});
		if (!confirm.isConfirmed) return;

		setAddingToRequestList(true);
		try {
			const payload = items.map((item) => ({
				user_id: user?.user_id,
				site_number: siteNumber,
				machine_id: Number(item.machine_id),
				product_id: Number(item.product_id),
			}));
			await saveRequestListBulk(payload);
		} finally {
			setAddingToRequestList(false);
		}
	}, [activePurchaseRequestNotification, ensurePurchaseRequestSiteReady, purchaseRequestSubmission, t, user?.user_id]);

	const handleOrderPurchaseRequestItems = React.useCallback(async (items) => {
		const purchasableItems = items.filter((item) => item.purchasable);
		const nonPurchasableItems = items.filter((item) => !item.purchasable);
		const canHandlePurchasable = canShowAddToCart && purchasableItems.length > 0;
		const canHandleNonPurchasable = canShowAddToRequestList && nonPurchasableItems.length > 0;
		let mode = null;
		if (canHandlePurchasable && canHandleNonPurchasable) {
			const result = await Swal.fire({
				title: 'Order Items',
				text: 'Some items are non-purchasable. Choose how to proceed.',
				input: 'radio',
				inputOptions: {
					both: 'Add purchasable to Cart and non-purchasable to Request List',
					cart: 'Add only purchasable items to Cart',
					request: 'Add only non-purchasable items to Request List',
				},
				inputValue: 'both',
				showCancelButton: true,
				confirmButtonText: 'Submit',
				cancelButtonText: t('BUTTON.CANCEL'),
			});
			if (!result.isConfirmed) return;
			mode = result.value || 'both';
		} else if (canHandlePurchasable) {
			mode = 'cart';
		} else if (canHandleNonPurchasable) {
			mode = 'request';
		} else {
			return;
		}
		if (mode === 'both') {
			await handleAddPurchaseRequestItemsToCart(purchasableItems);
			await handleAddPurchaseRequestItemsToRequestList(nonPurchasableItems);
			setOpen(false);
			navigate(ROUTES.CART);
			return;
		}
		if (mode === 'cart') {
			await handleAddPurchaseRequestItemsToCart(purchasableItems);
			setOpen(false);
			navigate(ROUTES.CART);
			return;
		}
		await handleAddPurchaseRequestItemsToRequestList(nonPurchasableItems);
		setOpen(false);
		if (canAccessRequestsPage) {
			navigate(ROUTES.REQUESTS);
		}
	}, [canAccessRequestsPage, canShowAddToCart, canShowAddToRequestList, handleAddPurchaseRequestItemsToCart, handleAddPurchaseRequestItemsToRequestList, navigate, t]);

	const handleNotificationRedirect = React.useCallback(async (notification, explicitTarget) => {
		const eventKey = String(notification?.event_key || "").toUpperCase();
		const isPurchaseRequest = eventKey === NotificationEvents.USER_PURCHASE_REQUEST_SUBMITTED;
		const isCcrEvent = eventKey.includes("CONTACT_CCR");
		if (!notification?.read_at && notification?.recipient_id) {
			await onMarkOne(notification.recipient_id);
		}
		if (isPurchaseRequest) {
			await handleOpenPurchaseRequestNotification(notification);
			return;
		}
		if (isCcrEvent) {
			setCcrSubmission(null);
			setCcrModalOpen(true);
			const submissionId = notification?.data?.ccr_submission_id;
			if (submissionId) {
				try {
					const submission = await fetchCcrSubmission(submissionId);
					if (submission) setCcrSubmission(submission);
				} catch { }
			}
			return;
		}

		// Check site access
		if (!hasAccessToNotificationSite(notification)) {
			const notifSite = resolveNotificationSiteNumber(notification);
			await Swal.fire({
				icon: 'warning',
				title: 'Access removed',
				text: `You no longer have access to the ${notifSite || 'selected'} location. Please contact system administrator for further details.`,
				toast: true,
				position: 'top-end',
				timer: 4000,
				showConfirmButton: false,
			});
			return;
		}

		const target = explicitTarget || getNotificationNavigationTarget(notification);
		if (!target || target === ROUTES.SUPPORT) return

		// Close the drawer before showing Swal or navigating
		setOpen(false);

		// Handle documentation notifications with site switching and machine filter
		if (typeof target === 'object' && target.requiresSiteSwitch) {
			const { path, siteId, machineNumber, state: targetState = {} } = target;

			// Resolve the site number from site_id
			let targetSiteNumber = null;
			if (siteId) {
				const siteRecord = siteData.find((s) => String(s.site_id) === String(siteId));
				targetSiteNumber = siteRecord?.customer_site_number || null;
			}

			// Check if site switch is needed
			const shouldPrompt = targetSiteNumber && selectedSite && String(targetSiteNumber) !== String(selectedSite);

			if (shouldPrompt) {
				const result = await Swal.fire({
					icon: 'info',
					title: 'Switch Customer Location?',
					html: `
					<p>This document is located at a different customer location.</p>
					<p><strong>Current:</strong> ${selectedSite}</p>
					<p><strong>Document Location:</strong> ${targetSiteNumber}</p>
				`,
					showCancelButton: true,
					confirmButtonText: 'Yes, Switch & View Documents',
					cancelButtonText: 'Cancel',
					allowOutsideClick: false,
					allowEscapeKey: true,
				});

				if (!result.isConfirmed) return;
			}

			// Switch site if needed
			if (shouldPrompt && targetSiteNumber) {
				await updateSite(String(targetSiteNumber), siteData || []);
				await new Promise(resolve => setTimeout(resolve, 300));
			}

			Swal.close();
			await new Promise(resolve => setTimeout(resolve, 100));

			navigate(path, {
				state: {
					...targetState,
					machineNumber: machineNumber,
					fromNotification: true,
					siteId: targetSiteNumber || siteId
				}
			});
			return;
		}

		// Handle regular navigation with site switching prompt
		const notifSite = resolveNotificationSiteNumber(notification);
		const shouldPrompt = notifSite && selectedSite && String(notifSite) !== String(selectedSite) && !/^https?:/i.test(target);

		if (shouldPrompt) {
			const result = await Swal.fire({
				icon: 'warning',
				title: 'Switch Customer Location?',
				text: 'This will navigate to a different site location.',
				showCancelButton: true,
				confirmButtonText: 'Yes, Switch Customer Location',
				cancelButtonText: 'Cancel',
				allowOutsideClick: false,
				allowEscapeKey: true,
			});
			if (!result.isConfirmed) return;
			await updateSite(String(notifSite), siteData || []);
			await new Promise(resolve => setTimeout(resolve, 300));
		}

		Swal.close();
		await new Promise(resolve => setTimeout(resolve, 100));

		// Navigate to target
		if (/^https?:/i.test(target)) {
			window.location.href = target;
		} else {
			navigate(target);
		}
	}, [handleOpenPurchaseRequestNotification, hasAccessToNotificationSite, navigate, onMarkOne, resolveNotificationSiteNumber, selectedSite, siteData, updateSite]);

	/**
	 * Clear every read notification for this user, server-side.
	 *
	 * Deliberately sends no id list. The old version collected recipient ids out of `items`,
	 * but the list endpoint caps a page at 100 rows, so with ~100 read notifications it could
	 * only ever cover the loaded page — and since it wrote to localStorage instead of the API,
	 * it cleared nothing at all. Both problems are the server's to answer, not the client's.
	 *
	 * @returns {Promise<void>} Resolves once the rows are gone and the page has been signalled.
	 */
	const handleClearRead = React.useCallback(async () => {
		await clearAllRead();
		notifyClearedElsewhere();
	}, [clearAllRead]);

	return (
		<Box sx={{ position: 'relative' }}>
			<ModalWrapper
				open={ccrModalOpen}
				setOpen={setCcrModalOpen}
				title={t('GENERAL.CONTACT_CCR_SUBMISSION')}
				contentWidth={720}
				content={<>
					{ccrSubmission ? (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
								<Box sx={{ minWidth: 200 }}>
									<Typography variant="caption" color="text.secondary">Submitted</Typography>
									<Typography variant="body2">{formatDateTime(ccrSubmission.created_at)}</Typography>
								</Box>
								{ccrSubmission.site_number && (
									<Box sx={{ minWidth: 200 }}>
										<Typography variant="caption" color="text.secondary">Site</Typography>
										<Typography variant="body2">{ccrSubmission.site_name} - {ccrSubmission.site_number}</Typography>
									</Box>
								)}
							</Box>
							<Divider />
							<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
								<Box>
									<Typography variant="caption" color="text.secondary">Name</Typography>
									<Typography variant="body2">{ccrSubmission.name || '-'}</Typography>
								</Box>
								<Box>
									<Typography variant="caption" color="text.secondary">{t('TABLE_HEADERS.EMAIL')}</Typography>
									<Typography variant="body2">{ccrSubmission.email || '-'}</Typography>
								</Box>
								<Box>
									<Typography variant="caption" color="text.secondary">{t('LABELS.PHONE')}</Typography>
									<Typography variant="body2">{ccrSubmission.phone || '-'}</Typography>
								</Box>
								<Box>
									<Typography variant="caption" color="text.secondary">Machine Number</Typography>
									<Typography variant="body2">{ccrSubmission.machine_number || '-'}</Typography>
								</Box>
								<Box sx={{ gridColumn: '1 / -1' }}>
									<Typography variant="caption" color="text.secondary">Subject</Typography>
									<Typography variant="body2">{ccrSubmission.subject || '-'}</Typography>
								</Box>
								<Box sx={{ gridColumn: '1 / -1' }}>
									<Typography variant="caption" color="text.secondary">Message</Typography>
									<Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{ccrSubmission.message || '-'}</Typography>
								</Box>
							</Box>
						</Box>
					) : (
						<Alert severity="info">{t('ALERT_MESSAGES.NO_DETAILS_FOUND_FOR_SUBMISSION')}</Alert>
					)}
				</>}
			/>
			<ModalWrapper
				open={purchaseRequestModalOpen}
				setOpen={setPurchaseRequestModalOpen}
				title="Purchase Request"
				subtitle="Review request details and requested parts."
				width="720px"
				content={
					(() => {
						const notification = activePurchaseRequestNotification;
						const submission = purchaseRequestSubmission;
						const data = notification?.data || {};
						const items = getPurchaseRequestItems(notification, submission);
						const recipients = Array.isArray(submission?.recipients) ? submission.recipients : [];
						const siteNumber = resolvePurchaseRequestSiteNumber(notification, submission) || '-';
						const siteName = submission?.site?.site_name || data?.site_name || '-';
						const requestedBy = getPurchaseRequestSender(notification, submission) || '-';
						return (
							<Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 2 }}>
								{purchaseRequestLoading && (
									<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
										<CircularProgress size={22} />
									</Box>
								)}
								<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
									<Box>
										<Typography variant="caption" color="text.secondary">Requested By</Typography>
										<Typography variant="body2">{requestedBy}</Typography>
									</Box>
									<Box>
										<Typography variant="caption" color="text.secondary">Site</Typography>
										<Typography variant="body2">{siteName} ({siteNumber})</Typography>
									</Box>
									<Box>
										<Typography variant="caption" color="text.secondary">Requested At</Typography>
										<Typography variant="body2">{notification?.created_at ? formatDateTime(notification.created_at) : '-'}</Typography>
									</Box>
								</Box>
								<Box>
									<Typography variant="caption" color="text.secondary">Message</Typography>
									<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
										{String(submission?.message || data?.message || notification?.body || '-')}
									</Typography>
								</Box>
								<Box>
									<Typography variant="caption" color="text.secondary">Recipients</Typography>
									{recipients.length ? (
										<Stack spacing={0.5}>
											{recipients.map((recipient, idx) => (
												<Typography key={`${recipient.user_id || idx}`} variant="body2">
													{recipient.user_name || recipient.user_email || `User ${recipient.user_id}`} {recipient.user_email ? `(${recipient.user_email})` : ''}
												</Typography>
											))}
										</Stack>
									) : <Typography variant="body2">-</Typography>}
								</Box>
								<Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5 }}>
									<Typography variant="subtitle2" sx={{ mb: 1 }}>Requested Parts ({items.length})</Typography>
									{items.length ? (
										<TableContainer sx={{ maxHeight: 320 }}>
											<Table size="small" stickyHeader>
												<TableHead>
													<TableRow>
														<TableCell>Part Name</TableCell>
														<TableCell align="right">Quantity</TableCell>
														<TableCell align="right">Price</TableCell>
														<TableCell align="right">Total</TableCell>
														<TableCell align="right">Actions</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{items.map((item, index) => (
														<TableRow key={`${item.product_id}-${index}`}>
															<TableCell>
																<Box sx={{ display: "flex", alignItems: "center", columnGap: 1 }}>
																	<img
																		src={resolveProductImageSrc(item)}
																		alt={item.product_name || item.product_number || "Part"}
																		onError={(event) => {
																			event.currentTarget.onerror = null;
																			event.currentTarget.src = stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;
																		}}
																		style={{ width: 28, height: 28, objectFit: "contain" }}
																	/>
																	<Box sx={{ minWidth: 0 }}>
																		{item.product_id && item.machine_id ? (
																			<Link
																				underline="hover"
																				sx={{
																					cursor: "pointer",
																					fontSize: "0.9rem",
																					display: "block",
																					maxWidth: 220,
																					whiteSpace: "nowrap",
																					overflow: "hidden",
																					textOverflow: "ellipsis",
																				}}
																				title={item.product_name || "-"}
																				onClick={() => navigate(`/machines/${item.machine_id}/${item.product_id}`)}
																			>
																				{item.product_name || '-'}
																			</Link>
																		) : (
																			<Typography
																				variant="body2"
																				sx={{
																					maxWidth: 220,
																					whiteSpace: "nowrap",
																					overflow: "hidden",
																					textOverflow: "ellipsis",
																				}}
																				title={item.product_name || "-"}
																			>
																				{item.product_name || '-'}
																			</Typography>
																		)}
																		<Typography
																			variant="caption"
																			color="text.secondary"
																			sx={{
																				display: "block",
																				maxWidth: 220,
																				whiteSpace: "nowrap",
																				overflow: "hidden",
																				textOverflow: "ellipsis",
																			}}
																			title={`Part #: ${item.product_number || item.product_id}`}
																		>
																			Part #: {item.product_number || item.product_id}
																		</Typography>
																	</Box>
																</Box>
															</TableCell>
															<TableCell align="right">x {item.quantity}</TableCell>
															<TableCell align="right">{formatCurrency(item.unit_price || 0, selectedSiteCurrency)}</TableCell>
															<TableCell align="right">{formatCurrency((item.unit_price || 0) * Number(item.quantity || 0), selectedSiteCurrency)}</TableCell>
															<TableCell align="right">
																{((item.purchasable && canShowAddToCart) || (!item.purchasable && canShowAddToRequestList)) && (
																	<Tooltip title={item.purchasable ? "Add to Cart" : "Add to Request List"}>
																		<span>
																			<IconButton
																				color="primary"
																				size="small"
																				onClick={() => {
																					if (item.purchasable) {
																						handleAddPurchaseRequestItemsToCart([item]);
																					} else {
																						handleAddPurchaseRequestItemsToRequestList([item]);
																					}
																				}}
																				disabled={addingToCart || addingToRequestList || (!item.purchasable && !item.machine_id)}
																			>
																				{item.purchasable ? <AddShoppingCart fontSize="small" /> : <SummarizeIcon fontSize="small" />}
																			</IconButton>
																		</span>
																	</Tooltip>
																)}
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										</TableContainer>
									) : (
										<Alert severity="info">No valid items in this request.</Alert>
									)}
								</Box>
							</Box>
						);
					})()
				}
				actions={
					<>
						<Button variant="outlined" onClick={() => setPurchaseRequestModalOpen(false)} disabled={addingToCart || addingToRequestList}>
							{t('BUTTON.CANCEL')}
						</Button>
						<Button
							variant="contained"
							onClick={() => handleOrderPurchaseRequestItems(getPurchaseRequestItems(activePurchaseRequestNotification, purchaseRequestSubmission))}
							disabled={addingToCart || addingToRequestList || !getPurchaseRequestItems(activePurchaseRequestNotification, purchaseRequestSubmission).length || (!canShowAddToCart && !canShowAddToRequestList)}
						>
							{addingToCart || addingToRequestList ? 'Processing...' : 'Order Items'}
						</Button>
					</>
				}
			/>


			<Box sx={{ position: 'relative', display: 'inline-block' }}>
				<Tooltip
					title={unreadTooltipLabel}
					arrow>
					<Badge color="error" variant="dot"
						anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
						sx={{
							'& .MuiBadge-badge': {
								top: 2,
								right: 2,
								transform: 'scale(1) translate(40%, -40%)', // 👈 pushes outside but touching
							},
						}}
						invisible={visibleUnreadCount <= 0}>
						<Button
							ref={buttonRef}
							onClick={toggle}
							color="primary"
							variant="roundedGreyBgSquareContained"
							aria-label={unreadTooltipLabel || t('UI_TEXT.NO_UNREAD_NOTIFICATIONS')}
						>
							{visibleUnreadCount > 0 ? (
							<Notifications sx={{ fontSize: '20px' }} />
							) : (
								<NotificationsNoneOutlined sx={{ fontSize: '20px' }} />
							)}
						</Button>
					</Badge>
				</Tooltip>
			</Box>

			{/* Popup */}
			<Popper
				open={showNew && !open}
				anchorEl={buttonRef.current}
				placement="bottom-end"
				transition
				sx={{ zIndex: 1300 }}
			>
				{({ TransitionProps }) => (
					<Fade {...TransitionProps} timeout={250}>
						<Paper
							onClick={toggle}
							sx={{
								p: 1.5,
								mt: '5px',
								boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
								borderRadius: 2,
								bgcolor: 'background.paper',
								color: 'text.primary',
								position: 'relative',
								minWidth: 220,
								cursor: 'pointer',
							}}
						>
							<Stack direction="row" spacing={1} alignItems="center">
								<Notifications color="primary" />
								<Typography variant="body1" fontWeight={"bold"}>
									{newNotificationText}
								</Typography>
								{latestNotification && isDifferentSite(latestNotification) && (
									<Chip size="small" sx={{ height: 18, fontSize: 10 }} color="warning" label="Different Customer Location" />
								)}
							</Stack>
							{latestNotification && isDifferentSite(latestNotification) && (
								<Typography variant="caption" color="text.secondary">
									{`Site: ${resolveNotificationSiteNumber(latestNotification)}`}
								</Typography>
							)}
							<Box
								ref={arrowRef}
								sx={{
									position: 'absolute',
									width: 12,
									height: 12,
									top: -6,
									right: 10,
									bgcolor: 'background.paper',
									transform: 'rotate(45deg)',
									boxShadow: '-2px -2px 7px 0px rgba(0, 0, 0, 0.1)',
									zIndex: 1,
								}}
							/>
						</Paper>
					</Fade>
				)}
			</Popper>

			<Drawer
				anchor="right"
				open={open}
				onClose={() => setOpen(false)}
				disableEnforceFocus={false}
				disableAutoFocus={false}
				disableRestoreFocus={false}
				// Properly manage focus when closing
				// slotProps={{
				// 	backdrop: {
				// 		onClick: setOpen(false)
				// 	}
				// }}
				PaperProps={{
					sx: {
						width: { xs: "100vw", sm: "85vw", md: 420, lg: "28vw" },
						maxWidth: { xs: "100vw", sm: 520 },
						minWidth: 300,
						height: "100dvh",
						maxHeight: "100dvh",
						overflow: "hidden",
						p: { xs: 0.75, sm: 1 },
						display: "flex",
						flexDirection: "column",
						zIndex: 1053
					},
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', px: 1, pt: 1, gap: 1 }}>
					<Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', minWidth: 0, rowGap: 1, flex: 1 }}>
						<Typography variant="h6" fontWeight={'bold'}>{t('UI_TEXT.NOTIFICATIONS_1')}</Typography>
						<Chip size="small" color={visibleUnreadCount > 0 ? 'warning' : 'default'} label={visibleUnreadCount > 0 ? `${visibleUnreadCount} ${t('UI_TEXT.UNREAD_1')}` : t('GEOGRAPHIC.ALL_CAUGHT_UP')} />
						<Chip size="small" variant="outlined" label={sortOrder} />
					</Stack>
					<Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
						<Tooltip title={sortOrder === t('GEOGRAPHIC.NEWEST_FIRST') ? t('GENERAL.SORT_OLDEST_FIRST') : t('GENERAL.SORT_NEWEST_FIRST')}>
							<Button variant="roundedGreyBgSquareContained" onClick={() => setSortOrder(prev => prev === t('GEOGRAPHIC.NEWEST_FIRST') ? t('GEOGRAPHIC.OLDEST_FIRST') : t('GEOGRAPHIC.NEWEST_FIRST'))}>
								<SortIcon />
							</Button>
						</Tooltip>
						<Button variant="roundedGreyBgSquareContained" onClick={() => { setOpen(false); navigate('/notifications'); }}><VisibilityIcon /></Button>
						<IconButton size="small" onClick={() => setOpen(false)} aria-label="Close notifications">
							<CloseIcon />
						</IconButton>
					</Stack>
				</Box>
				<Divider sx={{ my: 1 }} />
				<Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ px: 1 }}>
					<Tab label={`${t('UI_TEXT.UNREAD_1')}${visibleUnreadCount > 0 ? ` (${visibleUnreadCount})` : ''}`} value="unread" />
					<Tab label={`${t('GENERAL.READ_2')}${readTabCount > 0 ? ` (${readTabCount})` : ''}`} value="read" />
				</Tabs>

				<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: { xs: 0, sm: 1 }, pb: 1 }}>
					{dataToShow.length > 0 ? (
						<>
							<List dense sx={{ width: '100%' }}>
								{dataToShow.map((n, idx) => {
								const meta = getIconForType(n.event_key);
								const isRead = !!n.read_at;
								const canAccess = hasAccessToNotificationSite(n);
								const route = canAccess ? getNotificationNavigationTarget(n) : null;
								const isPurchaseRequest = isPurchaseRequestNotification(n);
								const isClickable = Boolean(route || isPurchaseRequest);
								const differentSite = isDifferentSite(n);
								const notifSite = resolveNotificationSiteNumber(n);
								return (
									<ListItem key={n.recipient_id || n.id || idx} disablePadding secondaryAction={
										<Stack direction="row" spacing={0.5} alignItems="center">
											{isRead ? (
												<Tooltip title={t('UI_TEXT.MARK_AS_UNREAD')}>
													<span>
														<IconButton edge="end"  size="small" onClick={() => onMarkUnreadDebounced(n.recipient_id)} disabled={!n.recipient_id}>
															<MarkEmailUnreadIcon  fontSize="small" color={'primary'} />
														</IconButton>
													</span>
												</Tooltip>
											) : (
												<Tooltip title={t('UI_TEXT.MARK_AS_READ')}>
													<span>
														<IconButton edge="end" size="small" onClick={() => onMarkOneDebounced(n.recipient_id)} disabled={!n.recipient_id}>
															<MarkEmailReadIcon fontSize="small" color={'success'} />
														</IconButton>
													</span>
												</Tooltip>
											)}
											{tab === 'read' && (
												<Tooltip title={'Clear'}>
													<span>
														<IconButton edge="end" size="small" onClick={() => onClear(n.recipient_id)} disabled={!n.recipient_id}>
															<ClearIcon fontSize="small" />
														</IconButton>
													</span>
												</Tooltip>
											)}
										</Stack>
									}>
										<ListItemButton
											onClick={() => handleNotificationRedirect(n, route)}
											disableRipple={!isClickable}
											sx={{
												alignItems: 'flex-start',
												cursor: isClickable ? 'pointer' : 'default',
												py: 1,
												borderLeft: differentSite ? '3px solid #f59e0b' : '3px solid transparent',
												backgroundColor: differentSite ? 'rgba(245,158,11,0.08)' : 'transparent',
											}}
										>
											<ListItemIcon sx={{ minWidth: 44, mt: 0.3 }}>
												<Button variant="roundedGreyBgSquareContained" size="small">
													{meta.icon}
												</Button>
											</ListItemIcon>
											<ListItemText
												primary={
													<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
														<Typography variant="body2" fontWeight={600} sx={{ color: isRead ? 'text.secondary' : 'text.primary' }}>{n.title || n.event_key}</Typography>

													</Box>
												}
												secondary={
													(() => {
														const desc = n.description || (n.data && n.data.description) || '';
														const body = n.body || '';
														const eventKey = String(n?.event_key || "").toUpperCase();
														const isPurchaseRequest = eventKey === NotificationEvents.USER_PURCHASE_REQUEST_SUBMITTED;
														const isDocumentNotification =
															eventKey === NotificationEvents.USER_NEW_DOCUMENT_ADDED ||
															eventKey === NotificationEvents.USER_DOCUMENT_MODIFIED;
														const parts = [];
														if (desc && !isDocumentNotification) parts.push(desc);
														if (isPurchaseRequest) {
															const compact = buildPurchaseRequestCardSummary(n);
															if (compact) parts.push(compact);
														} else if (body && (isDocumentNotification || body !== desc)) {
															parts.push(body);
														}
														if (differentSite && notifSite) parts.push(`Site: ${notifSite}`);
														parts.push(formatTime(n.created_at));

														return (
															<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, width: '95%' }}>
																<Typography variant="caption" color="text.secondary">
																	{parts.filter(Boolean).join(' • ')}
																</Typography>
																{differentSite && (
																	<Typography fontSize="11px" fontStyle="italic" color="warning.main">
																		Different Customer Location
																	</Typography>
																)}
															</Box>
														);
													})()
												}
											/>
										</ListItemButton>
									</ListItem>
								);
								})}
							</List>
							{hasMoreNotifications && (
								<Box sx={{ display: 'flex', justifyContent: 'center', px: 2, pb: 1 }}>
									<Button
										variant="outlined"
										onClick={() => setVisibleCount((prev) => prev + DRAWER_PAGE_SIZE)}
									>
										View More
									</Button>
								</Box>
							)}
						</>
					) : (
						<Box sx={{ px: 2, mt: 1 }}>
							<Alert severity="info">{t('UI_TEXT.NO_NOTIFICATIONS_YET')}</Alert>
						</Box>
					)}
				</Box>
				<Divider sx={{ mt: 1 }} />
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						flexWrap: 'wrap',
						rowGap: 1,
						px: 1,
						py: 1,
					}}
				>
					<Button variant="outlined" onClick={() => setOpen(false)} startIcon={<CloseIcon />}>{t('BUTTON.CLOSE')}</Button>
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
						{tab === 'read' ? (
							<Button variant="contained" onClick={handleClearRead} startIcon={<MarkEmailReadIcon />}>
								{t('UI_TEXT.CLEAR_READ')}
							</Button>
						) : (
							<>
								<Button variant="contained" onClick={refresh} disabled={busy} startIcon={<RefreshIcon />}>{t("BUTTON.REFRESH")}</Button>
								<Button
									variant="contained"
									onClick={async () => {
										// No id gathering: the server resolves the full unread set. The old
										// `if (ids.length)` guard also blocked the call whenever the loaded
										// page was already read but unread rows remained beyond it.
										try {
											await markAllRead();
										} catch { }
										await refresh();
									}}
									disabled={visibleUnreadCount === 0}
									startIcon={<MarkEmailReadIcon />}
								>
									{t('UI_TEXT.MARK_ALL_READ')}
								</Button>
							</>
						)}
					</Box>
				</Box>

			</Drawer>
		</Box>
	);
};

export default NotificationBell;
